import React, { useEffect, useState } from 'react';
import { MonitorSmartphone, LogOut, Loader2, ShieldCheck, Smartphone, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { authAPI } from '../services/api';
import { useT } from '../services/i18n';

const fmt = (iso, locale) => { try { return new Date(iso).toLocaleString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

// « Appareils connectés » — active sessions of the customer with per-device and global sign-out
const ConnectedDevices = () => {
  const { t, lang } = useT();
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => authAPI.sessions().then(setSessions).catch(() => setSessions([]));
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    setBusy(id);
    try { await authAPI.revokeSession(id); toast.success(t('sess.revoked')); await load(); } catch { toast.error(t('sess.error')); } finally { setBusy(''); }
  };
  const revokeAll = async () => {
    if (!window.confirm(t('sess.confirmAll'))) return;
    setBusy('all');
    try { const r = await authAPI.revokeOtherSessions(); toast.success(t('sess.revokedAll', { n: r.revoked })); await load(); } catch { toast.error(t('sess.error')); } finally { setBusy(''); }
  };
  const others = (sessions || []).filter(s => !s.current).length;

  return (
    <section className="bg-white rounded-xl shadow-sm p-6" data-testid="sessions-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><MonitorSmartphone className="text-orange-500" size={20} /> {t('sess.title')}</h2>
        <Button variant="outline" onClick={revokeAll} disabled={busy === 'all' || others === 0} data-testid="sessions-revoke-all" className="border-red-300 text-red-700 hover:bg-red-50">
          {busy === 'all' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <LogOut size={14} className="mr-1" />} {t('sess.logoutEverywhere')}{others ? ` (${others})` : ''}
        </Button>
      </div>
      <p className="text-xs text-gray-500 mb-4">{t('sess.hint')}</p>
      {sessions === null ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" /></div> : (
        <ul className="divide-y divide-gray-100" data-testid="sessions-list">
          {sessions.map(s => {
            const mobile = /iPhone|iPad|Android/.test(s.device);
            return (
              <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`session-${s.id}`}>
                <span className={`p-2 rounded-lg ${s.current ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{mobile ? <Smartphone size={18} /> : <Monitor size={18} />}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                    {s.device}
                    {s.current && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-green-100 text-green-700 rounded-full px-2 py-0.5" data-testid="session-current-badge"><ShieldCheck size={11} /> {t('sess.thisDevice')}</span>}
                  </p>
                  <p className="text-xs text-gray-500">{t('sess.lastSeen')} {fmt(s.last_seen_at, lang)} · IP {s.ip || '—'}{s.remember_me ? ` · ${t('sess.remembered')}` : ''}</p>
                </div>
                {!s.current && (
                  <button type="button" onClick={() => revoke(s.id)} disabled={busy === s.id} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`session-revoke-${s.id}`}>
                    {busy === s.id ? <Loader2 size={12} className="animate-spin" /> : <LogOut size={12} />} {t('sess.logout')}
                  </button>
                )}
              </li>
            );
          })}
          {sessions.length === 0 && <li className="py-3 text-sm text-gray-400" data-testid="sessions-empty">{t('sess.empty')}</li>}
        </ul>
      )}
    </section>
  );
};

export default ConnectedDevices;
